import * as React from 'react';
import { useSelector } from 'react-redux';
import { ModalWindow, UIHeading2 } from '@cian/ui-kit';

import {
  InfoPlate,
  InsideIcon,
  RateInfo,
  InfrastructureFromBuilder,
  InfrastructureTypeRows,
} from '../../../../components/Map/parts/TransportAccessibility';
import { MODAL_Z_INDEX } from '../../../../constants/infrastructure';
import { MAX_TRANSPORT_ACCESSIBILITY_RATE } from '../../../../constants/transportAccessibility';
import { getInfrastructureFromBuilder, getTransportAccessibilitySource } from '../../../../selectors/newbuilding';
import { IInfrastructureFromBuilder } from '../../../../types/newbuilding/transportAccessibility';
import { prepareRate } from '../../../TransportAccessibilityRate/helpers';
import { getInsideRows } from './helpers';
import {
  selectRegularInfrastructure,
  selectTransportAccessibilityInfrastructure,
} from '../../../../selectors/infrastructure';
import { ETransportAccessibilityTab, TTAInfrastructureType } from '../../../../types/map';
import { trackDepartmentLinkClick, trackInfrastructureClick, trackRateClick } from '../../analytics';

export interface ITransportAccessibilityOutsideProps {
  onTransportAccessibilityChange: (infrastructureType: TTAInfrastructureType | null) => void;
}

export const TransportAccessibilityInside: React.FC<ITransportAccessibilityOutsideProps> = ({
  onTransportAccessibilityChange,
}) => {
  const { rate, title, rateDetails } = useSelector(getInfrastructureFromBuilder) as IInfrastructureFromBuilder;
  const departmentLink = useSelector(getTransportAccessibilitySource);

  const displayRate = prepareRate(rate);
  const displayMaxRate = String(MAX_TRANSPORT_ACCESSIBILITY_RATE);

  const transportAccesibilityInfrastructure = useSelector(selectTransportAccessibilityInfrastructure);
  const { clickableRows, nonClickableRows } = React.useMemo(
    () => getInsideRows(transportAccesibilityInfrastructure),
    [transportAccesibilityInfrastructure],
  );

  const regularInfrastructure = useSelector(selectRegularInfrastructure);

  const [isModalOpen, setModal] = React.useState(false);

  const openModal = React.useCallback(() => {
    setModal(true);
    trackRateClick(ETransportAccessibilityTab.Inside);
  }, []);

  const closeModal = React.useCallback(() => setModal(false), []);

  const [selectedType, setSelectedType] = React.useState(clickableRows[0]?.id);

  React.useEffect(() => {
    onTransportAccessibilityChange(selectedType as TTAInfrastructureType);

    return () => onTransportAccessibilityChange(null);
  }, [onTransportAccessibilityChange]);

  const handleRowClick = React.useCallback(
    (infrastructureType: TTAInfrastructureType) => {
      onTransportAccessibilityChange(infrastructureType);
      setSelectedType(infrastructureType);

      trackInfrastructureClick({
        infrastructureType,
        infrastructureItems: regularInfrastructure,
      });
    },
    [regularInfrastructure],
  );

  const handleDepartmentLinkClick = React.useCallback(() => {
    trackDepartmentLinkClick(ETransportAccessibilityTab.Inside);
  }, []);

  return (
    <>
      <InfoPlate
        icon={<InsideIcon />}
        content={<RateInfo rate={rate} />}
        data-testid="TransportAccessibilityInsideInfo"
        withHorizontalMargin
        onClick={openModal}
      />
      <InfrastructureTypeRows
        clickableRows={clickableRows}
        nonClickableRows={nonClickableRows}
        onRowClick={handleRowClick}
        selectedItemId={selectedType}
      />
      <ModalWindow
        content={
          <InfrastructureFromBuilder
            departmentLink={departmentLink}
            maxRate={displayMaxRate}
            onDepartmentLinkClick={handleDepartmentLinkClick}
            rate={displayRate}
            terms={rateDetails}
          />
        }
        header={<UIHeading2>{title}</UIHeading2>}
        escape
        width={759}
        onClose={closeModal}
        open={isModalOpen}
        size="M"
        zIndex={MODAL_Z_INDEX}
      />
    </>
  );
};
